import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/components/ui/use-toast";
import { Button } from '@/components/ui/button';
import { Activity, BarChart3, Clock, CheckCircle2, Filter, Star, Users, RefreshCw, UserCircle2, Lock, TrendingUp } from 'lucide-react';
import DashboardLoading from '@/components/dashboard/DashboardLoading';
import MetricCard from '@/components/dashboard/MetricCard';
import LeadStatusChart from '@/components/dashboard/LeadStatusChart';
import LeadCategoryChart from '@/components/dashboard/LeadCategoryChart';
import RecentLeadsTable from '@/components/dashboard/RecentLeadsTable';
import { useDashboardData } from '@/hooks/useDashboardData';
import { canAccessAnalytics } from '@/services/subscriptions/access';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const { user, activeCompany } = useAuth();
  const { toast } = useToast();
  const [showUserLeads, setShowUserLeads] = useState(true);
  const [hasAnalyticsAccess, setHasAnalyticsAccess] = useState<boolean | null>(null);
  
  const { leads, isLoading, metrics, refreshData } = useDashboardData(user?.id, activeCompany?.id, showUserLeads);

  useEffect(() => {
    checkAnalyticsAccess();
  }, [user]);

  const checkAnalyticsAccess = async () => {
    const access = await canAccessAnalytics();
    setHasAnalyticsAccess(access);
  };

  const handleRefresh = () => {
    toast({
      title: "Refreshing dashboard",
      description: "Loading the latest data..."
    });
    refreshData();
  };

  const toggleLeadsFilter = () => {
    setShowUserLeads(prev => !prev);
  };

  if (isLoading) {
    return <DashboardLoading />;
  }

  return (
    <main className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 w-full">
      {/* Page Header */}
      <div className="mb-6 sm:mb-8 p-4 sm:p-6 rounded-xl bg-gradient-to-r from-primary/5 via-accent/5 to-background border border-primary/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              Dashboard
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {showUserLeads ? 'Your leads overview' : 'All company leads'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLeadsFilter}
              className="w-full sm:w-auto h-9 sm:h-10 justify-center"
            >
              {showUserLeads ? <Users className="h-4 w-4 sm:mr-2" /> : <UserCircle2 className="h-4 w-4 sm:mr-2" />}
              <span className="ml-2 sm:ml-0 text-xs sm:text-sm">{showUserLeads ? 'View All' : 'View Mine'}</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh} 
              className="w-full sm:w-auto h-9 sm:h-10 justify-center"
            >
              <RefreshCw className="h-4 w-4 sm:mr-2" />
              <span className="ml-2 sm:ml-0 text-xs sm:text-sm">Refresh</span>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5 md:p-6">
            <MetricCard
              title="Total Leads"
              value={metrics.totalLeads}
              icon={Users}
            />
          </CardContent>
        </Card>
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5 md:p-6">
            <MetricCard
              title="New Leads"
              value={metrics.newLeads}
              icon={Filter}
              iconColor="blue-500"
            />
          </CardContent>
        </Card>
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5 md:p-6">
            <MetricCard
              title="Favorite Leads"
              value={metrics.favoriteLeads}
              icon={Star}
              iconColor="yellow-500"
            />
          </CardContent>
        </Card>
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5 md:p-6">
            <MetricCard
              title="Avg. Rating"
              value={metrics.avgRating.toFixed(1)}
              icon={BarChart3}
              iconColor="green-500"
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5 md:p-6">
            <MetricCard
              title="Recent Activities"
              value={metrics.recentActivities}
              icon={Activity}
              iconColor="purple-500"
              description="In the last 7 days"
            />
          </CardContent>
        </Card>
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5 md:p-6">
            <MetricCard
              title="Pending Tasks"
              value={metrics.pendingTasks}
              icon={Clock}
              iconColor="orange-500"
              description="Tasks awaiting completion"
            />
          </CardContent>
        </Card>
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5 md:p-6">
            <MetricCard
              title="Completed Tasks"
              value={metrics.completedTasks}
              icon={CheckCircle2}
              iconColor="green-500"
              description="Successfully completed tasks"
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Charts Section */}
      {hasAnalyticsAccess ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          <Card className="shadow-sm">
            <CardContent className="p-4 sm:p-5 md:p-6">
              <div className="min-h-[250px] sm:min-h-[300px] md:min-h-[350px]">
                <LeadStatusChart data={metrics.leadsByStatus} />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4 sm:p-5 md:p-6">
              <div className="min-h-[250px] sm:min-h-[300px] md:min-h-[350px]">
                <LeadCategoryChart data={metrics.leadsByCategory} />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="bg-gradient-to-br from-purple-50 via-violet-50 to-purple-50/30 dark:from-purple-900/10 dark:via-violet-900/10 dark:to-purple-900/5 border-primary/20 mb-6 sm:mb-8">
          <CardContent className="text-center py-12 sm:py-16 px-4 sm:px-6">
            <div className="max-w-lg mx-auto space-y-4 sm:space-y-6">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 sm:p-6 rounded-2xl w-16 h-16 sm:w-20 sm:h-20 mx-auto flex items-center justify-center shadow-lg shadow-primary/10">
                <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">Advanced Analytics</h3>
                <p className="text-sm sm:text-base md:text-lg text-muted-foreground">
                  Unlock detailed charts with the Pro plan
                </p>
              </div>
              <Link to="/subscription">
                <Button size="lg" className="w-full sm:w-auto shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                  Upgrade to Pro
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Recent Leads Table */}
      <Card className="shadow-sm">
        <CardContent className="p-3 sm:p-4 md:p-6">
          <RecentLeadsTable leads={leads} />
        </CardContent>
      </Card>
    </main>
  );
};

export default Dashboard;
